import { useState } from 'react';
import {
  Server,
  Database,
  Zap,
  Activity,
  Cloud,
  Users,
  Globe,
  Smartphone,
  Monitor,
  X,
} from 'lucide-react';
import { Handle, Position } from 'reactflow';

// Shared constants
export const COMPONENT_ICONS = {
  compute: Server,
  storage: Database,
  cache: Zap,
  queue: Activity,
  loadbalancer: Cloud,
  database: Database,
  networking: Cloud,
  monitoring: Activity,
  security: Activity,
  users: Users,
  client: Users,
};

export const CATEGORY_COLORS = {
  compute: '#3b82f6',
  storage: '#10b981',
  cache: '#eab308',
  queue: '#8b5cf6',
  loadbalancer: '#06b6d4',
  database: '#10b981',
  networking: '#0ea5e9',
  monitoring: '#f97316',
  security: '#ef4444',
  users: '#ec4899',
  client: '#ec4899',
};

// Handle positions shared by all component nodes
const COMPONENT_HANDLE_POSITIONS = [
  { id: 't1', position: Position.Top, style: { left: '20%' } },
  { id: 't2', position: Position.Top, style: { left: '50%' } },
  { id: 't3', position: Position.Top, style: { left: '80%' } },
  { id: 'b1', position: Position.Bottom, style: { left: '20%' } },
  { id: 'b2', position: Position.Bottom, style: { left: '50%' } },
  { id: 'b3', position: Position.Bottom, style: { left: '80%' } },
  { id: 'l1', position: Position.Left, style: { top: '25%' } },
  { id: 'l2', position: Position.Left, style: { top: '50%' } },
  { id: 'l3', position: Position.Left, style: { top: '75%' } },
  { id: 'r1', position: Position.Right, style: { top: '25%' } },
  { id: 'r2', position: Position.Right, style: { top: '50%' } },
  { id: 'r3', position: Position.Right, style: { top: '75%' } },
];

// Handle positions for users node (only on right side)
const USERS_HANDLE_POSITIONS = [
  { id: 'r1', position: Position.Right, style: { top: '25%' } },
  { id: 'r2', position: Position.Right, style: { top: '50%' } },
  { id: 'r3', position: Position.Right, style: { top: '75%' } },
];

/**
 * ComponentNode - Renders a system component in the canvas
 * @param {Object} data - Node data including category, label, qps, monthlyCost, etc.
 * @param {string} id - Node ID
 * @param {boolean} readOnly - If true, disables interactivity (for preview mode)
 */
export const ComponentNode = ({ data, id, readOnly = false }) => {
  const Icon = COMPONENT_ICONS[data.category] || Server;
  const [isHovered, setIsHovered] = useState(false);
  const categoryColor = CATEGORY_COLORS[data.category] || '#6b7280';

  const handleNodeClick = (e) => {
    if (readOnly) return;
    // Don't trigger if clicking on remove button
    if (e.target.closest('button')) return;
    if (data.onShowDetails) {
      data.onShowDetails({ ...data, nodeId: id });
    }
  };

  return (
    <div
      onMouseEnter={() => !readOnly && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleNodeClick}
      className={`relative px-3 py-2 rounded-lg border-2 bg-white shadow-md min-w-[140px] transition-all ${
        !readOnly ? 'cursor-pointer' : ''
      } ${isHovered && !readOnly ? 'shadow-xl scale-105' : ''}`}
      style={{ borderColor: categoryColor }}
    >
      {COMPONENT_HANDLE_POSITIONS.map(({ id: handleId, position, style }) => (
        <Handle
          key={handleId}
          id={handleId}
          type="source"
          position={position}
          style={{ ...style, width: 8, height: 8, background: categoryColor, border: '2px solid white' }}
          isConnectable={!readOnly}
        />
      ))}

      {isHovered && !readOnly && data.onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); data.onRemove?.(); }}
          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow z-10"
        >
          <X className="w-3 h-3" />
        </button>
      )}

      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded" style={{ backgroundColor: categoryColor + '20' }}>
          <Icon className="w-4 h-4" style={{ color: categoryColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-xs truncate">{data.label}</div>
          <div className="text-[10px] text-gray-500">
            {data.qps ? `${(data.qps/1000).toFixed(0)}k QPS` : `$${data.monthlyCost}/mo`}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * UsersNode - Renders the users/clients entry point in the canvas
 * @param {Object} data - Node data including users, concurrent, label, etc.
 * @param {string} id - Node ID
 * @param {boolean} readOnly - If true, disables interactivity (for preview mode)
 */
export const UsersNode = ({ data, id, readOnly = false }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Parse user counts from data
  const userCount = data.users || data.userCount || '50M';
  const concurrent = data.concurrent || data.concurrentSessions || '5M';

  // Only allow click if onShowDetails is defined (admin mode)
  const isConfigurable = !readOnly && !!data.onShowDetails;

  const handleNodeClick = (e) => {
    if (readOnly || !data.onShowDetails) return;
    // Don't trigger if clicking on remove button
    if (e.target.closest('button')) return;
    data.onShowDetails({ ...data, nodeId: id, nodeType: 'users' });
  };

  return (
    <div
      onMouseEnter={() => !readOnly && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleNodeClick}
      className={`relative rounded-2xl border-2 shadow-lg min-w-[180px] overflow-hidden transition-all duration-300 ${
        isConfigurable ? 'cursor-pointer' : ''
      } ${isHovered && !readOnly ? 'shadow-2xl scale-105' : ''}`}
      style={{
        borderColor: '#ec4899',
        background: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 50%, #fbcfe8 100%)'
      }}
    >
      {USERS_HANDLE_POSITIONS.map(({ id, position, style }) => (
        <Handle
          key={id}
          id={id}
          type="source"
          position={position}
          style={{
            ...style,
            width: 10,
            height: 10,
            background: '#ec4899',
            border: '2px solid white',
            boxShadow: '0 0 6px rgba(236, 72, 153, 0.5)'
          }}
          isConnectable={!readOnly}
        />
      ))}

      {isHovered && !readOnly && data.onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); data.onRemove?.(); }}
          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow z-10"
        >
          <X className="w-3 h-3" />
        </button>
      )}

      {/* Header with animated gradient */}
      <div
        className="px-4 py-2 text-white font-bold text-sm flex items-center gap-2"
        style={{
          background: 'linear-gradient(90deg, #ec4899, #8b5cf6, #ec4899)',
          backgroundSize: '200% 100%',
          animation: !readOnly ? 'gradientShift 3s ease infinite' : 'none',
        }}
      >
        <Globe className="w-4 h-4" />
        <span>{data.label || 'Users & Clients'}</span>
      </div>

      {/* User icons row with animation */}
      <div className="px-4 py-3 flex justify-center">
        <div className="flex -space-x-2">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="w-8 h-8 rounded-full border-2 border-white shadow-md flex items-center justify-center"
              style={{
                background: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'][i],
                animation: !readOnly ? `bounce 1s ease-in-out ${i * 0.1}s infinite` : 'none',
                animationPlayState: isHovered ? 'running' : 'paused'
              }}
            >
              {i < 3 ? (
                <Users className="w-4 h-4 text-white" />
              ) : i === 3 ? (
                <Smartphone className="w-4 h-4 text-white" />
              ) : (
                <Monitor className="w-4 h-4 text-white" />
              )}
            </div>
          ))}
          <div className="w-8 h-8 rounded-full border-2 border-white shadow-md flex items-center justify-center bg-slate-600 text-white text-xs font-bold">
            +{userCount}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 pb-3 grid grid-cols-2 gap-2 text-xs">
        <div className="bg-white/60 rounded-lg px-2 py-1.5 text-center">
          <div className="text-pink-600 font-bold">{userCount}</div>
          <div className="text-gray-600">Total Users</div>
        </div>
        <div className="bg-white/60 rounded-lg px-2 py-1.5 text-center">
          <div className="text-purple-600 font-bold">{concurrent}</div>
          <div className="text-gray-600">Concurrent</div>
        </div>
      </div>

      {/* Traffic indicator */}
      {!readOnly && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <div className="flex gap-0.5">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="w-1.5 h-3 bg-pink-400 rounded-full"
                  style={{
                    animation: `pulse 1s ease-in-out ${i * 0.15}s infinite`,
                    opacity: 0.4 + (i * 0.2)
                  }}
                />
              ))}
            </div>
            <span>Traffic flowing →</span>
          </div>
        </div>
      )}

      {/* Animated pulse ring */}
      {!readOnly && (
        <div
          className="absolute -inset-1 rounded-2xl pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(236,72,153,0.2) 0%, transparent 70%)',
            animation: 'pulse 2s ease-in-out infinite'
          }}
        />
      )}
    </div>
  );
};

// CSS animations component - include this once in the app
export const NodeAnimationStyles = () => (
  <style>{`
    @keyframes gradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-4px); }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.7; transform: scale(1.02); }
    }
  `}</style>
);

/**
 * Create node types object for ReactFlow
 * @param {boolean} readOnly - If true, creates read-only node types
 */
export const createNodeTypes = (readOnly = false) => ({
  component: (props) => <ComponentNode {...props} readOnly={readOnly} />,
  users: (props) => <UsersNode {...props} readOnly={readOnly} />,
  default: (props) => <ComponentNode {...props} readOnly={readOnly} />,
  custom: (props) => <ComponentNode {...props} readOnly={readOnly} />,
});
